<script lang="ts">
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { peopleApi } from '$lib/api/crm';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { formatIST } from '$lib/utils/dates';
  import { contactOwnerLabel, ownershipUserLabel, transferValidation, handoverCacheKeys } from '$lib/utils/contactOwnership';
  import type { Person } from '$lib/types';

  let { person, onTransferred }: { person: Person; onTransferred: () => void } = $props();
  const client = useQueryClient();
  let assigning = $state(false), showHistory = $state(false), error = $state('');
  let destination = $state(0);
  let owner = $state<number | undefined>();
  let note = $state(''), moveDeals = $state('');
  const canTransfer = $derived(session.isAdmin || !!session.me?.user.intake_coordinator || person.owner_user_id === session.me?.user.id);
  const salesPipeline = $derived(['lighting', 'homeowners', 'dealers'].includes(session.me?.verticals?.find(row => row.id === person.vertical_id)?.slug ?? ''));
  const owners = createQuery(() => ({
    queryKey: ['contact-owners', salesPipeline ? 'sales' : destination, session.me?.user.id, session.me?.user.role, session.me?.verticals?.map(row => row.id).join(',')], queryFn: () => salesPipeline ? peopleApi.salesOwners() : peopleApi.owners(destination), enabled: assigning
  }));
  const history = createQuery(() => ({
    queryKey: ['ownership-history', person.id], queryFn: () => peopleApi.ownershipHistory(person.id), enabled: showHistory
  }));
  const transfer = createMutation(() => ({
    mutationFn: () => peopleApi.transfer(person.id, { owner_user_id: owner!, vertical_id: destination, note: note.trim(), move_open_deals: moveDeals === 'yes' }),
    onSuccess: () => {
      // Close before re-fetch: a cross-pipeline handover may remove our access.
      assigning = false;
      onTransferred();
      for (const key of handoverCacheKeys) client.invalidateQueries({ queryKey: [key] });
      toast.success('Contact handed over. It may no longer be visible in your accessible pipelines.');
    },
    onError: (err: unknown) => error = getErrorMessage(err)
  }));
  function submit(e: SubmitEvent) {
    e.preventDefault();
    error = transferValidation(owner, destination, owners.isError ? [] : (owners.data?.results ?? []).map(row => row.id), note, moveDeals);
    if (!error) transfer.mutate();
  }
  function pipelineName(id: number) { return session.me?.verticals.find(row => row.id === id)?.name ?? `Pipeline #${id}`; }
</script>

<section class="day-card space-y-3 text-sm">
  <p class="font-semibold text-navy">Contact owner: {contactOwnerLabel(person)}</p>
  <p>{pipelineName(person.vertical_id)} · The contact owner is responsible for sales follow-up, not the company group owner or meeting coordinator.</p>
  <div class="flex flex-wrap gap-2">
    {#if canTransfer}<button type="button" class="btn-secondary" disabled={transfer.isPending} onclick={() => { assigning = !assigning; destination = person.vertical_id; owner = undefined; note = ''; moveDeals = ''; error = ''; }}>Assign / transfer contact</button>{/if}
    <button type="button" class="btn-secondary" onclick={() => showHistory = !showHistory}>Ownership history</button>
  </div>
  {#if assigning}
    <form class="space-y-3 border-t border-[#eeedea] pt-3" onsubmit={submit}>
      <label class="label" for="handover-pipeline">Destination pipeline</label>
      {#if salesPipeline}
        <p id="handover-pipeline" class="font-semibold">{pipelineName(destination)}</p>
        <p class="text-xs">Choose the responsible salesperson below. Their pipeline is selected automatically; administrators and coordinators are not sales-owner choices.</p>
      {:else}
      <select id="handover-pipeline" class="input" bind:value={destination} onchange={() => owner = undefined} disabled={transfer.isPending}>
        {#each session.me?.verticals ?? [] as pipeline}<option value={pipeline.id}>{pipeline.name}</option>{/each}
      </select>
      {/if}
      <label class="label" for="handover-owner">Responsible salesperson / contact owner</label>
      <select id="handover-owner" class="input" bind:value={owner} onchange={() => { const selected = owners.data?.results.find(row => row.id === owner); if (selected?.vertical_id) destination = selected.vertical_id; }} required disabled={owners.isFetching || owners.isError || transfer.isPending}>
        <option value={undefined}>Choose an eligible owner</option>
        {#each owners.data?.results ?? [] as user}<option value={user.id}>{user.name || user.email}</option>{/each}
      </select>
      {#if owners.isError}<p role="alert">{getErrorMessage(owners.error)}</p><button type="button" class="btn-secondary" onclick={() => owners.refetch()}>Retry owners</button>
      {:else if owners.isLoading}<p>Loading eligible owners…</p>
      {:else if !owners.data?.results.length}<p>No configured sales owner is available in your accessible pipelines.</p>{/if}
      <label class="label" for="handover-note">Required handover note</label>
      <textarea id="handover-note" class="input" bind:value={note} required disabled={transfer.isPending} placeholder="Enquiry, agreed next step and meeting arrangements"></textarea>
      <label class="label" for="handover-deals">Move linked open deals?</label>
      <select id="handover-deals" class="input" bind:value={moveDeals} required disabled={transfer.isPending}>
        <option value="">Choose explicitly</option><option value="no">No — leave open deals unchanged</option><option value="yes">Yes — move open deals with this handover</option>
      </select>
      <p class="text-xs">Linked data can prevent a cross-pipeline transfer. Any server rejection will be shown here; nothing is treated as transferred until accepted.</p>
      {#if error}<p role="alert" class="text-red-700">{error}</p>{/if}
      <button class="btn-primary" type="submit" disabled={transfer.isPending || owners.isFetching || owners.isError}>{transfer.isPending ? 'Transferring…' : 'Confirm handover'}</button>
    </form>
  {/if}
  {#if showHistory}
    {#if history.isLoading}<p>Loading ownership history…</p>
    {:else if history.isError}<p role="alert">{getErrorMessage(history.error)}</p><button type="button" class="btn-secondary" onclick={() => history.refetch()}>Retry history</button>
    {:else}
      <ol class="space-y-3">
        {#each history.data?.results ?? [] as entry (entry.id)}
          <li class="border-t border-[#eeedea] pt-2">
            <p>{ownershipUserLabel(entry.from_owner_user_id, entry.from_owner_name)} → {ownershipUserLabel(entry.to_owner_user_id, entry.to_owner_name)}</p>
            <p class="text-xs">{pipelineName(entry.from_vertical_id)} → {pipelineName(entry.to_vertical_id)} · {formatIST(entry.created_at)} · By {ownershipUserLabel(entry.actor_user_id, entry.actor_name)}</p>
            <p class="whitespace-pre-wrap">{entry.note}</p>
          </li>
        {:else}<li>No ownership changes recorded.</li>{/each}
      </ol>
    {/if}
  {/if}
</section>
